import { useReducer } from "react";

type FilterReducerState<T> = {
  groupedFilterMap: { [key: string]: T[] | null };
  emailMobileKeys: string[] | null;
  currentKey: string | null;
};

type FilterActions<T> =
  | {
      type: "SET_FILTER_MAP";
      payload: { [key: string]: T[] };
    }
  | {
      type: "SET_FILTER_KEY";
      payload: string;
    };

const groupFilteredData = <T extends { email: string; mobile: string }>(
  rowData: T[]
) => {
  const uniqueEmailMobileMap: {
    [key: string]: T[];
  } = {};
  for (let i = 0; i < rowData.length; ++i) {
    const { email, mobile } = rowData[i];
    if (!email || !mobile) continue;
    const currentKey = `${email.trim()}-${mobile.trim()}`;
    if (uniqueEmailMobileMap[currentKey]) {
      uniqueEmailMobileMap[currentKey].push(rowData[i]);
    } else {
      uniqueEmailMobileMap[currentKey] = [rowData[i]];
    }
  }
  return uniqueEmailMobileMap;
};

const mobileEmailFilterReducer = <T>(
  state: FilterReducerState<T>,
  action: FilterActions<T>
) => {
  const { groupedFilterMap, emailMobileKeys, currentKey } = state;
  switch (action.type) {
    case "SET_FILTER_MAP":
      const uniqueKeys = Object.keys(action.payload);
      return {
        ...state,
        groupedFilterMap: action.payload,
        emailMobileKeys: uniqueKeys,
        currentKey: !!currentKey ? currentKey : uniqueKeys[0],
      };
    case "SET_FILTER_KEY":
      return {
        ...state,
        currentKey: action.payload,
      };
    default:
      throw Error(`Invalid action`);
  }
};

export const useMobileEmailFilter = <T>(rowData: T[]) => {
  const [filterState, filterDispatch] = useReducer(mobileEmailFilterReducer, {
    groupedFilterMap: null,
    emailMobileKeys: null,
    currentKey: null,
  });
  const { emailMobileKeys, currentKey, groupedFilterMap } = filterState;

  const setGroupFilterMap = (payload: ReturnType<typeof groupFilteredData>) => {
    filterDispatch({
      type: "SET_FILTER_MAP",
      payload: payload,
    });
  };

  return { filterState, setGroupFilterMap, filterDispatch };
};
